'use client';

import { Button } from '@components/ui/button';
import { isImageContentType, isPdfContentType } from '@files/constants';
import { formatFileSize } from '@files/format-size';
import { cn } from '@utils/cn';
import { FileText, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, useState } from 'react';
import FileActionsMenu from './FileActionsMenu';
import FileViewer from './FileViewer';
import { LazyPdfFirstPage } from './pdf/lazy';

export type AttachmentView = {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  sizeBytes?: number | null;
  width?: number | null;
  height?: number | null;
  previewUrl?: string;
  uploadedByUserId?: string | null;
};

export type AttachmentGalleryVariant = 'grid' | 'compact' | 'strip';

type AttachmentGalleryProps = {
  files: AttachmentView[];
  /**
   * `grid` — editor/detail pages; `compact` — chat messages; `strip` — the
   * composer's pending files (fixed square tiles with a remove badge).
   */
  variant?: AttachmentGalleryVariant;
  /** Strip only: discard a pending (not yet uploaded) file. */
  onRemove?: (id: string) => void;
  /** Whether the viewer/menu may offer Delete for a persisted file. */
  canDelete?: (file: AttachmentView) => boolean;
  onDelete?: (file: AttachmentView) => unknown;
};

const PDF_THUMB_WIDTH = 36;

const AttachmentGallery: FC<AttachmentGalleryProps> = ({
  files,
  variant = 'grid',
  onRemove,
  canDelete,
  onDelete
}) => {
  const t = useTranslations('files');
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  if (files.length === 0) {
    return null;
  }

  if (variant === 'strip') {
    return (
      <ul className="flex flex-wrap gap-2">
        {files.map(file => {
          const isImage = isImageContentType(file.contentType);
          const src = file.previewUrl || file.url;
          return (
            <li key={file.id} className="relative size-16 sm:size-20">
              <div
                title={file.filename}
                aria-label={file.filename}
                className="flex size-full items-center justify-center overflow-hidden rounded-md border border-border bg-muted"
              >
                {isImage && src ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={src}
                    alt={file.filename}
                    className="size-full object-cover"
                  />
                ) : (
                  <span className="flex flex-col items-center gap-0.5">
                    <FileText className="size-5 text-secondary-foreground" />
                    <span className="text-micro text-muted-foreground uppercase">
                      {isPdfContentType(file.contentType) ? t('pdf') : ''}
                    </span>
                  </span>
                )}
              </div>
              {onRemove ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute -top-1.5 -right-1.5 size-6 min-h-0 rounded-full border border-border bg-background after:absolute after:-inset-2 after:content-[''] lg:size-6"
                  onClick={() => onRemove(file.id)}
                  aria-label={t('remove', { name: file.filename })}
                >
                  <X className="size-3" />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  const compact = variant === 'compact';
  // Images and docs render in separate lists; the viewer walks one combined
  // list so prev/next crosses both.
  const images = files.filter(file => isImageContentType(file.contentType));
  const docs = files.filter(file => !isImageContentType(file.contentType));
  const ordered = [...images, ...docs];
  const openViewer = (file: AttachmentView) => {
    if (file.url) {
      setViewerIndex(ordered.findIndex(entry => entry.id === file.id));
    }
  };
  const menuTriggerClassName =
    'size-11 min-h-11 rounded-full bg-background/80 lg:size-11 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100';

  return (
    <div className="flex flex-col gap-2">
      {images.length > 0 ? (
        <ul
          className={cn(
            'grid gap-2',
            images.length === 1
              ? compact
                ? 'grid-cols-[minmax(0,20rem)]'
                : 'grid-cols-1'
              : 'grid-cols-2 sm:grid-cols-3'
          )}
        >
          {images.map(file => {
            const src = file.previewUrl || file.url;
            return (
              <li
                key={file.id}
                className={cn(
                  'group relative',
                  !compact && 'virtual-list-item virtual-list-item-image'
                )}
              >
                <button
                  type="button"
                  className={cn(
                    'block w-full overflow-hidden rounded-md border border-border bg-muted text-left',
                    compact
                      ? 'rounded-[var(--messenger-bubble-radius,12px)]'
                      : 'h-40'
                  )}
                  onClick={() => openViewer(file)}
                  aria-label={t('open-image', { name: file.filename })}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={src}
                    alt={file.filename}
                    className={
                      compact
                        ? 'h-auto max-h-80 w-full object-contain'
                        : 'size-full object-cover'
                    }
                    width={file.width ?? undefined}
                    height={file.height ?? undefined}
                    loading="lazy"
                  />
                </button>
                {file.url ? (
                  <div className="absolute top-1 right-1">
                    <FileActionsMenu
                      file={file}
                      canDelete={canDelete?.(file) ?? false}
                      onOpen={() => openViewer(file)}
                      onDelete={onDelete}
                      triggerClassName={menuTriggerClassName}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {docs.length > 0 ? (
        <ul className={cn('flex flex-col gap-1.5', compact && 'max-w-[24rem]')}>
          {docs.map(file => {
            const isPdf = isPdfContentType(file.contentType);
            const icon = (
              <FileText className="size-4 text-secondary-foreground" />
            );
            const inner = (
              <>
                <span className="flex h-12 w-9 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border bg-muted">
                  {isPdf && file.url ? (
                    <LazyPdfFirstPage
                      url={file.url}
                      width={PDF_THUMB_WIDTH}
                      fallback={icon}
                    />
                  ) : (
                    icon
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-label text-foreground">
                    {file.filename}
                  </span>
                  <span className="text-meta text-muted-foreground">
                    {isPdf ? t('pdf') : file.contentType}
                    {file.sizeBytes
                      ? ` · ${formatFileSize(file.sizeBytes)}`
                      : ''}
                  </span>
                </span>
              </>
            );
            return (
              <li
                key={file.id}
                className={cn(
                  'group flex min-w-0 items-center gap-2 rounded-md border border-border bg-card py-2 pr-1 pl-3',
                  compact
                    ? 'rounded-[var(--messenger-bubble-radius,12px)]'
                    : 'virtual-list-item'
                )}
              >
                {file.url ? (
                  <button
                    type="button"
                    onClick={() => openViewer(file)}
                    aria-label={t('open-file', { name: file.filename })}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left hover:bg-accent/40"
                  >
                    {inner}
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {inner}
                  </div>
                )}
                {file.url ? (
                  <FileActionsMenu
                    file={file}
                    canDelete={canDelete?.(file) ?? false}
                    onOpen={() => openViewer(file)}
                    onDelete={onDelete}
                    triggerClassName="size-8 min-h-0 lg:size-8"
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <FileViewer
        files={ordered}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
        canDelete={canDelete}
        onDelete={onDelete}
      />
    </div>
  );
};

export default AttachmentGallery;
